import type {
  CertificateDate,
  StudentCertificate,
} from "@core/certificates/types";
import {
  createLatinDateTimeFormatter,
  normalizeLatinDigits,
} from "@core/shared/utils/locale-formatters";

/** Certificates issued within this window still count as "new" (badge + celebration). */
export const NEW_CERTIFICATE_WINDOW_MS = 30 * 24 * 60 * 60 * 1000;

export type RankMedal = {
  emoji: string;
  className: string;
};

const medals: Record<number, RankMedal> = {
  1: {
    emoji: "🥇",
    className:
      "bg-amber-100 text-amber-800 ring-amber-300 dark:bg-amber-950 dark:text-amber-200 dark:ring-amber-700",
  },
  2: {
    emoji: "🥈",
    className:
      "bg-slate-100 text-slate-700 ring-slate-300 dark:bg-slate-800 dark:text-slate-200 dark:ring-slate-600",
  },
  3: {
    emoji: "🥉",
    className:
      "bg-orange-100 text-orange-800 ring-orange-300 dark:bg-orange-950 dark:text-orange-200 dark:ring-orange-700",
  },
};

const otherRank: RankMedal = {
  emoji: "🏅",
  className:
    "bg-violet-100 text-violet-800 ring-violet-300 dark:bg-violet-950 dark:text-violet-200 dark:ring-violet-700",
};

/**
 * Gold / silver / bronze medal for ranks 1-3, a generic medal otherwise.
 *
 * @example
 * const medal = rankMedal(1); // { emoji: "🥇", ... }
 */
export function rankMedal(rank?: number | null): RankMedal | null {
  if (!rank || !Number.isFinite(rank) || rank < 1) return null;

  return medals[rank] || otherRank;
}

/** Epoch milliseconds of a Warlock date object or an ISO string. */
export function certificateTimestamp(
  value?: CertificateDate | string | null,
): number | undefined {
  if (!value) return undefined;

  if (typeof value === "string") {
    const parsed = Date.parse(value);

    return Number.isFinite(parsed) ? parsed : undefined;
  }

  return typeof value.timestamp === "number" && Number.isFinite(value.timestamp)
    ? value.timestamp
    : undefined;
}

/**
 * Human date in the active language with English digits.
 *
 * @example
 * formatCertificateDate("2026-10-06T19:00:00.000Z"); // "6 أكتوبر 2026"
 */
export function formatCertificateDate(
  value?: CertificateDate | string | null,
): string {
  const timestamp = certificateTimestamp(value);

  if (timestamp === undefined) {
    return typeof value === "object" && value?.format
      ? normalizeLatinDigits(String(value.format))
      : "";
  }

  return normalizeLatinDigits(
    createLatinDateTimeFormatter({
      day: "numeric",
      month: "long",
      year: "numeric",
    }).format(new Date(timestamp)),
  );
}

/** The certificate's own issue-date text («6 أكتوبر 2026»), else a formatted fallback. */
export function certificateDateText(certificate: StudentCertificate): string {
  const value = certificate.values?.date;

  if (typeof value === "string" && value.trim()) {
    return normalizeLatinDigits(value.trim());
  }

  return formatCertificateDate(certificate.issuedAt);
}

/** Reason line printed on the certificate (may be empty). */
export function certificateReason(certificate: StudentCertificate): string {
  const value = certificate.values?.reason;

  return typeof value === "string" ? normalizeLatinDigits(value.trim()) : "";
}

/** Whether the certificate was issued recently enough to be celebrated. */
export function isRecentCertificate(
  certificate: Pick<StudentCertificate, "issuedAt">,
  now = Date.now(),
): boolean {
  const timestamp = certificateTimestamp(certificate.issuedAt);

  return (
    timestamp !== undefined && now - timestamp <= NEW_CERTIFICATE_WINDOW_MS
  );
}

/** Rank text («المركز الأول») from the API, else a generic «المركز N». */
export function certificateRankText(
  rank?: number | null,
  rankLabel?: string | null,
  fallback?: (rank: number) => string,
): string {
  if (rankLabel && rankLabel.trim()) return normalizeLatinDigits(rankLabel);
  if (!rank || rank < 1) return "";

  return fallback ? fallback(rank) : String(rank);
}
